import { yupResolver } from "@hookform/resolvers/yup";
import { iCity } from "core/kangarooTypes/iCity";
import { X } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import Button from "shared/Button";
import { Dialog } from "shared/Dialog/Dialog";
import { DialogContent } from "shared/Dialog/DialogContent";
import { DialogFooter } from "shared/Dialog/DialogFooter";
import { DialogHeader } from "shared/Dialog/DialogHeader";
import { DialogTitle } from "shared/Dialog/DialogTitle";
import FormInput from "shared/FormInput";
import * as yup from "yup";
import { useTranslation } from "react-i18next";

// Props for the CityFormDialog component
interface CityFormDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: iCity) => void;
  initialData?: iCity;
  isEditing: boolean;
}

// City Form Dialog Component
const CityFormDialog = ({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  isEditing,
}: CityFormDialogProps) => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Form validation schema for cities
  const CityFormSchema = yup.object({
    name: yup.string().required(t("cityFormDialog.form.validation.cityNameRequired")),
    price: yup
      .number()
      .required(t("cityFormDialog.form.validation.deliveryPriceRequired"))
      .min(0, t("cityFormDialog.form.validation.pricePositive")),
    discount: yup.number().default(0),
    old_price: yup.number().default(0),
  });
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<Omit<iCity, "id">>({
    resolver: yupResolver(CityFormSchema),
    defaultValues: initialData || {
      name: "",
      price: 0,
      discount: 0,
      old_price: 0,
    },
  });

  useEffect(() => {
    if (isOpen) {
      reset(
        initialData || {
          id: 0,
          name: "",
          price: 0,
          discount: 0,
          old_price: 0,
        }
      );
    }
  }, [initialData, reset, isOpen]);

  const handleFormSubmit = async (data: Omit<iCity, "id">) => {
    try {
      setIsSubmitting(true);
      const iCity: iCity = {
        ...data,
        id: initialData?.id || 0,
      };
      await onSubmit(iCity);
      handleClose();
    } catch (error) {
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    onClose();
    reset({
      name: "",
      price: 0,
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex justify-between items-center">
            <span>{isEditing ? t("cityFormDialog.title.edit") : t("cityFormDialog.title.add")}</span>
            <button
              onClick={handleClose}
              className="rounded-full p-1 hover:bg-gray-100 transition-colors"
            >
              <X size={18} />
            </button>
          </DialogTitle>
        </DialogHeader>

        <div className="p-6 flex flex-col justify-start h-full">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormInput
              label={t("cityFormDialog.form.cityName")}
              name="name"
              register={register}
              errors={errors}
              placeholder={t("cityFormDialog.form.cityNamePlaceholder")}
            />
            <FormInput
              label={t("cityFormDialog.form.deliveryPrice")}
              name="price"
              type="number"
              register={register}
              errors={errors}
              placeholder={t("cityFormDialog.form.deliveryPricePlaceholder")}
            />
          </div>
        </div>

        <DialogFooter className="mt-4 sm:mt-6 flex flex-col sm:flex-row gap-2">
          <Button
            type="button"
            variant="outlined"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            {t("cityFormDialog.buttons.cancel")}
          </Button>
          <Button
            onClick={handleSubmit(handleFormSubmit)}
            isLoading={isSubmitting}
          >
            {isEditing ? t("cityFormDialog.buttons.update") : t("cityFormDialog.buttons.add")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CityFormDialog;
